import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Slider,
  Alert,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { dashboardTheme } from '../styles/dashboardTheme';
import { getMyCheckInTemplate, submitTemplateCheckIn, createCheckIn } from '../../../services/api';

/**
 * CheckInDialog Component
 * Dynamic modal for creating check-ins based on template configuration
 * Supports both legacy (mood/anxiety) and dynamic templates from treatment pathways
 */
const CheckInDialog = ({ open, onClose, onSuccess, token }) => {
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [error, setError] = useState(null);

  // Legacy mode state (fallback when no template)
  const [moodRating, setMoodRating] = useState(3);
  const [anxietyRating, setAnxietyRating] = useState(3);
  const [notes, setNotes] = useState('');

  // Fetch template when dialog opens
  const fetchTemplate = useCallback(async () => {
    if (!open || !token) return;

    try {
      setLoadingTemplate(true);
      setError(null);
      const result = await getMyCheckInTemplate(token);
      if (result.success && result.template) {
        setTemplate(result.template);
        // Initialize responses with default values
        const initialResponses = {};
        for (const field of result.template.fields || []) {
          if (field.fieldType === 'boolean') {
            initialResponses[field.id] = false;
          } else if (field.fieldType === 'multi_select') {
            initialResponses[field.id] = [];
          } else if (field.fieldType === 'numeric_scale' || field.fieldType === 'emoji_scale') {
            // Support both 'config' (from schema) and 'scaleConfig' (from types) patterns
            const config = field.scaleConfig || field.config || { min: 0, max: 10 };
            // Default to middle of scale
            const mid = Math.floor((config.min + config.max) / 2);
            initialResponses[field.id] = mid;
          }
        }
        setResponses(initialResponses);
      } else {
        // No template - use legacy mode
        setTemplate(null);
      }
    } catch (err) {
      console.error('Error fetching check-in template:', err);
      // Fall back to legacy mode on error
      setTemplate(null);
    } finally {
      setLoadingTemplate(false);
    }
  }, [open, token]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  // Map 1-5 scale to emojis for mood (legacy)
  const moodEmojiMap = {
    1: { emoji: '😢', label: 'Very Low', color: '#9E9E9E' },
    2: { emoji: '😔', label: 'Low', color: '#90CAF9' },
    3: { emoji: '😐', label: 'Neutral', color: '#FFF176' },
    4: { emoji: '🙂', label: 'Good', color: '#AED581' },
    5: { emoji: '😊', label: 'Very High', color: '#66BB6A' }
  };

  // Map 1-5 scale to emojis for anxiety (legacy)
  const anxietyEmojiMap = {
    1: { emoji: '😌', label: 'Very Low', color: '#66BB6A' },
    2: { emoji: '🙂', label: 'Low', color: '#AED581' },
    3: { emoji: '😐', label: 'Moderate', color: '#FFF176' },
    4: { emoji: '😟', label: 'High', color: '#FFB74D' },
    5: { emoji: '😰', label: 'Very High', color: '#EF5350' }
  };

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const isFormValid = () => {
    if (!template) {
      // Legacy mode - always valid (ratings have defaults)
      return true;
    }

    // Check required fields
    const requiredFields = template.fields.filter(f => f.required);
    return requiredFields.every(field => {
      const value = responses[field.id];
      if (value === undefined || value === null) return false;
      if (field.fieldType === 'text' && value === '') return false;
      if (field.fieldType === 'multi_select' && value.length === 0) return false;
      return true;
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (template && template._id) {
        // Dynamic template submission
        await submitTemplateCheckIn(template._id, responses, token);
      } else {
        // Legacy submission
        const checkInData = {
          moodRating,
          anxietyRating,
          notes: notes.trim() || undefined
        };
        await createCheckIn(checkInData, token);
      }

      // Reset form
      setResponses({});
      setMoodRating(3);
      setAnxietyRating(3);
      setNotes('');

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      console.error('Error creating check-in:', err);
      if (err.response?.data?.message?.includes('already exists')) {
        setError('You have already submitted a check-in for today.');
      } else {
        setError(err.response?.data?.error || 'Failed to save check-in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setResponses({});
      setMoodRating(3);
      setAnxietyRating(3);
      setNotes('');
      setError(null);
      onClose();
    }
  };

  // Render a numeric scale field (0-10 or custom range)
  const renderNumericScale = (field) => {
    // Support both 'config' (from schema) and 'scaleConfig' (from types) patterns
    const config = field.scaleConfig || field.config || { min: 0, max: 10 };
    const value = responses[field.id] ?? config.min;
    const marks = [];
    for (let i = config.min; i <= config.max; i += (config.step || 1)) {
      marks.push({ value: i, label: String(i) });
    }

    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && <Typography component="span" color="error"> *</Typography>}
        </Typography>
        {field.helpText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {field.helpText}
          </Typography>
        )}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h2" sx={{ fontWeight: 600, color: dashboardTheme.colors.primary.main }}>
            {value}
          </Typography>
        </Box>
        {config.showLabels !== false && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {config.lowLabel || config.min}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {config.highLabel || config.max}
            </Typography>
          </Box>
        )}
        <Slider
          value={value}
          onChange={(e, newValue) => updateResponse(field.id, newValue)}
          min={config.min}
          max={config.max}
          step={config.step || 1}
          marks={marks.length <= 11 ? marks : undefined}
          valueLabelDisplay="auto"
          disabled={loading}
          sx={{
            color: dashboardTheme.colors.primary.main,
            '& .MuiSlider-thumb': { width: 24, height: 24 }
          }}
        />
      </Box>
    );
  };

  // Predefined emoji sets for different scales
  const emojiSets = {
    mood: ['😢', '😔', '😐', '🙂', '😊'],
    intensity: ['😌', '🙂', '😐', '😟', '😰'],
    agreement: ['👎', '🤔', '😐', '👍', '👏'],
  };

  // Render an emoji scale field
  const renderEmojiScale = (field) => {
    // Support both 'config' (from schema) and 'scaleConfig' (from types) patterns
    const config = field.scaleConfig || field.config || { min: 1, max: 5 };

    // Handle emojiSet as string (enum from schema) or array (from types)
    let emojis;
    if (Array.isArray(config.emojiSet)) {
      emojis = config.emojiSet;
    } else if (typeof config.emojiSet === 'string' && emojiSets[config.emojiSet]) {
      emojis = emojiSets[config.emojiSet];
    } else if (Array.isArray(config.customEmojis)) {
      emojis = config.customEmojis;
    } else {
      emojis = emojiSets.mood; // Default fallback
    }

    const value = responses[field.id];

    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && <Typography component="span" color="error"> *</Typography>}
        </Typography>
        {field.helpText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {field.helpText}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          {emojis.map((emoji, index) => {
            const emojiValue = config.min + index;
            return (
              <IconButton
                key={emojiValue}
                onClick={() => updateResponse(field.id, emojiValue)}
                disabled={loading}
                sx={{
                  fontSize: '2rem',
                  p: 1.5,
                  borderRadius: 2,
                  border: value === emojiValue ? `2px solid ${dashboardTheme.colors.primary.main}` : '2px solid transparent',
                  backgroundColor: value === emojiValue ? `${dashboardTheme.colors.primary.main}20` : 'transparent',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
              >
                {emoji}
              </IconButton>
            );
          })}
        </Box>
        {config.showLabels !== false && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {config.lowLabel || 'Low'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {config.highLabel || 'High'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Render a boolean toggle field
  const renderBoolean = (field) => {
    const value = responses[field.id] || false;

    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <FormControlLabel
          control={
            <Switch
              checked={value}
              onChange={(e) => updateResponse(field.id, e.target.checked)}
              disabled={loading}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {field.label}
                {field.required && <Typography component="span" color="error"> *</Typography>}
              </Typography>
              {field.helpText && (
                <Typography variant="body2" color="text.secondary">
                  {field.helpText}
                </Typography>
              )}
            </Box>
          }
        />
      </Box>
    );
  };

  // Render a single select field
  const renderSingleSelect = (field) => {
    const value = responses[field.id];
    // Support both field.options and field.config.options patterns
    const options = field.options || field.config?.options || [];

    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && <Typography component="span" color="error"> *</Typography>}
        </Typography>
        {field.helpText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {field.helpText}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {options.map(option => (
            <Chip
              key={option.value}
              label={option.emoji ? `${option.emoji} ${option.label}` : option.label}
              onClick={() => updateResponse(field.id, option.value)}
              disabled={loading}
              color={value === option.value ? 'primary' : 'default'}
              variant={value === option.value ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.95rem', py: 2 }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Render a multi select field
  const renderMultiSelect = (field) => {
    const selected = responses[field.id] || [];
    // Support both field.options and field.config.options patterns
    const options = field.options || field.config?.options || [];

    const toggleOption = (optionValue) => {
      const current = responses[field.id] || [];
      if (current.includes(optionValue)) {
        updateResponse(field.id, current.filter(v => v !== optionValue));
      } else {
        updateResponse(field.id, [...current, optionValue]);
      }
    };

    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && <Typography component="span" color="error"> *</Typography>}
        </Typography>
        {field.helpText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {field.helpText}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {options.map(option => (
            <Chip
              key={option.value}
              label={option.emoji ? `${option.emoji} ${option.label}` : option.label}
              onClick={() => toggleOption(option.value)}
              disabled={loading}
              color={selected.includes(option.value) ? 'primary' : 'default'}
              variant={selected.includes(option.value) ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.95rem', py: 2 }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Render a text field
  const renderText = (field) => {
    const placeholder = field.placeholder || field.config?.placeholder || 'Enter your response...';
    const maxLength = field.config?.maxLength || 500;

    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && <Typography component="span" color="error"> *</Typography>}
        </Typography>
        {field.helpText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {field.helpText}
          </Typography>
        )}
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder={placeholder}
          value={responses[field.id] || ''}
          onChange={(e) => updateResponse(field.id, e.target.value)}
          disabled={loading}
          inputProps={{ maxLength }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
          {(responses[field.id]?.length || 0)}/{maxLength}
        </Typography>
      </Box>
    );
  };

  // Render a number input field
  const renderNumber = (field) => {
    return (
      <Box sx={{ mb: 3 }} key={field.id}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && <Typography component="span" color="error"> *</Typography>}
        </Typography>
        {field.helpText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {field.helpText}
          </Typography>
        )}
        <TextField
          type="number"
          placeholder={field.placeholder || '0'}
          value={responses[field.id] ?? ''}
          onChange={(e) => updateResponse(field.id, e.target.value ? Number(e.target.value) : null)}
          disabled={loading}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, width: 120 }}
        />
      </Box>
    );
  };

  // Render a field based on its type
  const renderField = (field) => {
    switch (field.fieldType) {
      case 'numeric_scale':
        return renderNumericScale(field);
      case 'emoji_scale':
        return renderEmojiScale(field);
      case 'boolean':
        return renderBoolean(field);
      case 'single_select':
        return renderSingleSelect(field);
      case 'multi_select':
        return renderMultiSelect(field);
      case 'text':
        return renderText(field);
      case 'number':
        return renderNumber(field);
      default:
        return null;
    }
  };

  // Render legacy form (mood + anxiety)
  const renderLegacyForm = () => {
    const marks = [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: '5' }
    ];

    return (
      <>
        {/* Mood Rating Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Mood Rating
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant="h1"
              sx={{ fontSize: '4rem', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
            >
              {moodEmojiMap[moodRating].emoji}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: moodEmojiMap[moodRating].color }}
            >
              {moodEmojiMap[moodRating].label}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            1 = Very Low, 5 = Very High
          </Typography>
          <Slider
            value={moodRating}
            onChange={(e, newValue) => setMoodRating(newValue)}
            min={1}
            max={5}
            step={1}
            marks={marks}
            valueLabelDisplay="auto"
            disabled={loading}
            sx={{
              color: dashboardTheme.colors.primary.main,
              '& .MuiSlider-thumb': { width: 24, height: 24 }
            }}
          />
        </Box>

        {/* Anxiety Rating Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Anxiety Rating
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant="h1"
              sx={{ fontSize: '4rem', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
            >
              {anxietyEmojiMap[anxietyRating].emoji}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: anxietyEmojiMap[anxietyRating].color }}
            >
              {anxietyEmojiMap[anxietyRating].label}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            1 = Very Low, 5 = Very High
          </Typography>
          <Slider
            value={anxietyRating}
            onChange={(e, newValue) => setAnxietyRating(newValue)}
            min={1}
            max={5}
            step={1}
            marks={marks}
            valueLabelDisplay="auto"
            disabled={loading}
            sx={{
              color: anxietyRating <= 2 ? '#66BB6A' : anxietyRating >= 4 ? '#EF5350' : '#FFF176',
              '& .MuiSlider-thumb': { width: 24, height: 24 }
            }}
          />
        </Box>

        {/* Quick Selection Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
            Quick Selection:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                {[1, 2, 3, 4, 5].map((value) => (
                  <IconButton
                    key={`mood-${value}`}
                    onClick={() => setMoodRating(value)}
                    disabled={loading}
                    sx={{
                      fontSize: '1.5rem',
                      background: moodRating === value
                        ? dashboardTheme.colors.primary.main + '20'
                        : 'transparent',
                      '&:hover': { transform: 'scale(1.2)' }
                    }}
                  >
                    {moodEmojiMap[value].emoji}
                  </IconButton>
                ))}
              </Box>
              <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                Mood
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                {[1, 2, 3, 4, 5].map((value) => (
                  <IconButton
                    key={`anxiety-${value}`}
                    onClick={() => setAnxietyRating(value)}
                    disabled={loading}
                    sx={{
                      fontSize: '1.5rem',
                      background: anxietyRating === value ? '#FFB74D20' : 'transparent',
                      '&:hover': { transform: 'scale(1.2)' }
                    }}
                  >
                    {anxietyEmojiMap[value].emoji}
                  </IconButton>
                ))}
              </Box>
              <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                Anxiety
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Optional Notes */}
        <Box>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
            Optional Notes (How are you feeling today?):
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Share any thoughts about your mood or anxiety levels..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading}
            inputProps={{ maxLength: 500 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Box>
      </>
    );
  };

  // Render dynamic template form
  const renderDynamicForm = () => {
    const sortedFields = [...template.fields].sort((a, b) => a.order - b.order);

    return (
      <>
        {template.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {template.description}
          </Typography>
        )}
        {sortedFields.map(field => renderField(field))}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {template?.displayName || 'Daily Check-In'}
        </Typography>
        <IconButton onClick={handleClose} disabled={loading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingTemplate ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : template ? (
          renderDynamicForm()
        ) : (
          renderLegacyForm()
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ borderRadius: '12px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingTemplate || !isFormValid()}
          sx={{
            background: dashboardTheme.colors.primary.gradient,
            borderRadius: '12px',
            px: 4,
            '&:hover': { background: dashboardTheme.colors.primary.dark }
          }}
        >
          {loading ? 'Submitting...' : 'Submit Check-In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInDialog;
